import {inject, Injectable} from "@angular/core"
import type {CmLogger} from "@cm/log"
import {CM_LOGGER_API} from "@common/services/logging"
import {ContentTypeModel, PaymentState} from "@generated"
import {IsUnique} from "@cm/utils/filter"
import {fetchThrowingErrors} from "@common/helpers/api/fetch"
import {BatchApiCallById} from "@common/helpers/batch-api-call/batch-api-call-by-id"
import {ApiRequest} from "@common/models/api-request/api-request"
import {
    PaymentStateLabelForMaterialsGQL,
    PaymentStateLabelForModelsGQL,
    PaymentStateLabelForPicturesGQL,
    PaymentStateLabelForTemplatesGQL,
} from "@common/services/payment-state/payment.state.service.generated"

export type RequestPayload = {
    id: string
    __typename: ContentTypeModel
}

export type ResponsePayload = {
    id: string
    paymentState: PaymentState
}

export type BatchedRequestPayload = {
    __typename: ContentTypeModel
    requests: ApiRequest<RequestPayload, ResponsePayload>[]
}

@Injectable({
    providedIn: "root",
})
export class PaymentStateService extends BatchApiCallById<RequestPayload, ResponsePayload, BatchedRequestPayload> {
    private readonly logger: CmLogger = inject(CM_LOGGER_API)
    private readonly paymentStateLabelForMaterials = inject(PaymentStateLabelForMaterialsGQL)
    private readonly paymentStateLabelForModels = inject(PaymentStateLabelForModelsGQL)
    private readonly paymentStateLabelForPictures = inject(PaymentStateLabelForPicturesGQL)
    private readonly paymentStateLabelForTemplates = inject(PaymentStateLabelForTemplatesGQL)

    constructor() {
        super()
    }

    protected batchRequests(requests: ApiRequest<RequestPayload, ResponsePayload>[]): BatchedRequestPayload[] {
        // separate the requests by content-model-type
        const requestsByContentTypeModel = new Map<ContentTypeModel, BatchedRequestPayload>()
        requests.forEach((request) => {
            const __typename = request.payload.__typename
            if (!requestsByContentTypeModel.has(__typename)) {
                requestsByContentTypeModel.set(__typename, {__typename, requests: []})
            }
            requestsByContentTypeModel.get(request.payload.__typename)!.requests.push(request)
        })
        return Array.from(requestsByContentTypeModel.values())
    }

    protected async callApi(payload: BatchedRequestPayload): Promise<(ResponsePayload | undefined | null)[]> {
        const ids = payload.requests.map((request) => request.payload.id).filter(IsUnique)
        switch (payload.__typename) {
            case "Material":
                return fetchThrowingErrors(this.paymentStateLabelForMaterials)({ids}).then((response) => response.materials)
            case "Model":
                return fetchThrowingErrors(this.paymentStateLabelForModels)({ids}).then((response) => response.models)
            case "Picture":
                return fetchThrowingErrors(this.paymentStateLabelForPictures)({ids}).then((response) => response.pictures)
            case "Template":
                return fetchThrowingErrors(this.paymentStateLabelForTemplates)({ids}).then((response) => response.templates)
            default:
                this.logger.error(`Unsupported item type for payment state label: ${payload.__typename}`)
                return []
        }
    }
}
